import type { EvidenceData } from '@/components/citations';

/**
 * Citation from an extraction
 */
interface ExtractionCitation {
  id: string;
  artifact_id: string;
  citation_id?: string | null;
  block_id?: string | null;
  transcript_line_id?: string | null;
  excerpt?: string | null;
  created_at: string;
  modified_at: string;
}

/**
 * Extraction metadata stored in the 'extra' field
 */
interface ExtractionMetadata {
  confidence?: number | null;
  reasoning?: string | null;
  context?: string | null;
}

/**
 * Artifact extraction from the API
 */
interface ArtifactExtraction {
  id: string;
  artifact_id?: string | null;
  created_at: string;
  modified_at: string;
  extraction_type: string;
  extraction: any;
  extra?: ExtractionMetadata | null;
  citations: ExtractionCitation[];
}

/**
 * Suggestion from entity suggestions API
 */
interface Suggestion {
  id: string;
  created_at: string;
  modified_at: string;
  status: string;
  provenance_status: string;
  action: string;
  body?: any;
  extraction_ids: string[];
  extra?: any;
  target_id?: string | null;
  extractions: ArtifactExtraction[];
}

/**
 * Transform suggestions into evidence data for the citation viewer
 *
 * Strategy: Show ONE narrative combining context + reasoning, with confidence inline
 * - If we have reasoning, use it (it's more specific)
 * - Fall back to context (source description)
 * - excerpts provide the actual supporting text
 */
export const transformSuggestionsToEvidence = (
  suggestions: Suggestion[],
): EvidenceData[] =>
  suggestions.flatMap((suggestion) =>
    suggestion.extractions
      .filter((extraction) => extraction.artifact_id != null)
      .map((extraction) => {
        const metadata = extraction.extra || {};

        const citationIds = extraction.citations
          .map((c) => c.citation_id)
          .filter((id): id is string => id != null);

        const excerpts = extraction.citations
          .map((c) => c.excerpt)
          .filter(
            (excerpt): excerpt is string => excerpt != null && excerpt !== '',
          );

        // Consolidate: prefer reasoning over context, show one primary narrative
        const primaryNarrative = metadata.reasoning || metadata.context || null;

        return {
          artifact_id: extraction.artifact_id,
          citation_ids: citationIds.length > 0 ? citationIds : null,
          rationale: primaryNarrative,
          excerpts: excerpts.length > 0 ? excerpts : null,
          confidence: metadata.confidence ?? null,
          created_at: suggestion.created_at,
        };
      }),
  );
